import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { openEmail } from '@/lib/contact';
import { ArrowLeft, MessageSquare, Mail, Phone, Calendar, Plus, Edit, CheckCircle, Clock, AlertTriangle, FileText, Users, Truck, Wrench, Camera, BookOpen, CreditCard, ClipboardList, Link2, Copy, Table2, MapPin, BarChart3, GripVertical, Check, RotateCcw, ExternalLink, Unlink, Layers, Trash2, IndianRupee } from 'lucide-react';
import { useConfirm } from '@/components/ConfirmDialog';
import BOQEstimateEditor from '@/components/BOQEstimateEditor';
import InlineEdit from '@/components/InlineEdit';
import ClientBriefTab from '@/components/ClientBriefTab';
import TimeframeModule from '@/components/TimeframeModule';
import ProjectTasksBoard from '@/components/ProjectTasksBoard';
import ProjectDrawings from '@/components/ProjectDrawings';
import ProjectPhotos from '@/components/ProjectPhotos';
import ProjectBilling from '@/components/ProjectBilling';
import ProjectOrders from '@/components/ProjectOrders';
import ProjectMaterials from '@/components/ProjectMaterials';
import MeetingsTab from '@/components/MeetingsTab';
import DesignFeeTab from '@/components/DesignFeeTab';
import InvoiceDialog from '@/components/InvoiceDialog';
import { MeetingsProvider } from '@/contexts/MeetingsContext';
import WhatsAppDeepLinkButton from '@/components/WhatsAppDeepLinkButton';
import VendorSelect from '@/components/VendorSelect';
import InlineVendorEdit from '@/components/InlineVendorEdit';
import { useAuth } from '@/contexts/AuthContext';
import { isOwner, canFeature } from '@/lib/perms';

function formatINR(n) { if (!n) return '0'; const num = Number(n); if (num >= 100000) return `${(num/100000).toFixed(1)}L`; return num.toLocaleString('en-IN'); }
function formatDate(d) { if (!d) return '-'; const dt = new Date(d); return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`; }
const phases = ['Lead', 'Consultation', 'Design', 'Execution', 'Handover'];
const dsStatus = { Created: 'bg-[#E8E6E1] text-[#2C2C2E]', 'Sent to Client': 'bg-[#3A8C8C]/10 text-[#3A8C8C]', Revisions: 'bg-[#C67D5B]/10 text-[#C67D5B]', 'Client Approved': 'bg-[#7DA68A]/10 text-[#7DA68A]' };
const drawStatus = { Created: 'bg-[#E8E6E1] text-[#2C2C2E]', Submitted: 'bg-[#3A8C8C]/10 text-[#3A8C8C]', Approved: 'bg-[#7DA68A]/10 text-[#7DA68A]', 'Issued on Site': 'bg-[#C67D5B]/10 text-[#C67D5B]', Executed: 'bg-[#7DA68A]/15 text-[#7DA68A]' };
const snagStatus = { Open: 'bg-[#C4727F]/10 text-[#C4727F]', 'In Progress': 'bg-[#C67D5B]/10 text-[#C67D5B]', Resolved: 'bg-[#7DA68A]/10 text-[#7DA68A]' };

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const [confirm, confirmDialog] = useConfirm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canFinancials = canFeature(user, 'financials');
  const ownerOnly = isOwner(user); // proposals & invoices are Owner-only
  const canManage = isOwner(user) || canFeature(user, 'manageData');
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addType, setAddType] = useState('');
  const [addForm, setAddForm] = useState({});

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  // Reorder mode state
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderTabs, setReorderTabs] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);

  // Proposal link state
  const [proposals, setProposals] = useState([]);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showLinkProposal, setShowLinkProposal] = useState(false);
  const [proposalSearch, setProposalSearch] = useState('');

  // Vendor portal state
  const [vendorPortalModal, setVendorPortalModal] = useState(null); // { agency, link }
  // WhatsApp composer state — single dialog for both client and vendor flows
  const [whatsappTarget, setWhatsappTarget] = useState(null); // { kind: 'client'|'vendor', name, phone }
  const openClientWhatsApp = () => setWhatsappTarget({
    kind: 'client',
    name: project.client_name || 'there',
    phone: project.client_phone || '',
  });
  const openVendorWhatsApp = (agency) => setWhatsappTarget({
    kind: 'vendor',
    name: agency.name || 'there',
    phone: agency.contact || agency.phone || '',
  });
  const generateVendorPortal = async (agency) => {
    try {
      const res = await api.post(`/projects/${projectId}/vendor-portal/generate`, {
        vendor_id: agency.id || '',
        vendor_name: agency.name || '',
      });
      setVendorPortalModal({ agency, link: res.data });
    } catch { toast.error('Failed to generate portal link'); }
  };
  const copyVendorPortalUrl = (token) => {
    const url = `${window.location.origin}/vendor-portal/${token}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success('Vendor portal link copied'),
      () => toast.error('Copy failed')
    );
  };

  const loadProject = async () => {
    try { const res = await api.get(`/projects/${projectId}`); setProject(res.data); }
    catch { toast.error('Project not found'); navigate('/projects'); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadProject(); }, [projectId]);

  const updatePhase = async (phase) => {
    try { await api.put(`/projects/${projectId}`, { phase }); toast.success(`Phase: ${phase}`); loadProject(); }
    catch { toast.error('Update failed'); }
  };

  const updateStatus = async (status) => {
    try { await api.put(`/projects/${projectId}`, { status }); toast.success(`Status: ${status}`); loadProject(); }
    catch { toast.error('Update failed'); }
  };

  const openAdd = (type) => { setAddType(type); setAddForm({}); setShowAddDialog(true); };

  const submitAdd = async () => {
    // Validate per-type
    if (addType === 'agency' && !(addForm.name || '').trim()) {
      toast.error('Please select a vendor');
      return;
    }
    try {
      const endpoints = { meeting: 'meetings', billing: 'billing', order: 'orders', snag: 'snag-list', design: 'design-stages', drawing: 'drawings', agency: 'agencies' };
      await api.post(`/projects/${projectId}/${endpoints[addType]}`, addForm);
      toast.success('Added successfully');
      setShowAddDialog(false);
      loadProject();
    } catch { toast.error('Failed to add'); }
  };

  if (loading || !project) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  const updateField = async (field, value) => {
    try {
      await api.put(`/projects/${projectId}`, { [field]: value });
      setProject(prev => ({ ...prev, [field]: value }));
    } catch { toast.error('Update failed'); }
  };

  const updateSubItem = async (arrayField, itemId, updates) => {
    const items = project[arrayField]?.map(item =>
      item.id === itemId ? { ...item, ...updates } : item
    );
    try {
      await api.put(`/projects/${projectId}`, { [arrayField]: items });
      setProject(prev => ({ ...prev, [arrayField]: items }));
    } catch { toast.error('Update failed'); }
  };

  const removeAgency = async (agencyId) => {
    if (!(await confirm({ title: 'Remove vendor?', message: 'This vendor will be unlinked from the project.', confirmLabel: 'Remove' }))) return;
    try {
      await api.delete(`/projects/${projectId}/agencies/${agencyId}`);
      toast.success('Vendor removed');
      loadProject();
    } catch { toast.error('Failed to remove vendor'); }
  };

  const phaseIdx = phases.indexOf(project.phase);

  const defaultTabOrder = ['overview', 'brief', 'meetings', 'boq', 'timeframe', 'tasks', 'drawings', 'photos', 'orders', 'materials', 'billing', 'agencies', 'snags', 'portal'];

  const tabMeta = {
    overview: { label: 'Overview', icon: ClipboardList },
    brief: { label: 'Brief', icon: BookOpen },
    meetings: { label: 'Meetings', icon: Users },
    boq: { label: 'BOQ / Estimate', icon: Table2 },
    timeframe: { label: 'Timeframe', icon: BarChart3 },
    tasks: { label: 'Tasks', icon: CheckCircle },
    drawings: { label: 'Drawings', icon: FileText },
    photos: { label: 'Photos', icon: Camera },
    orders: { label: 'Orders', icon: Truck },
    materials: { label: 'Materials', icon: Layers },
    billing: { label: 'Billing', icon: CreditCard },
    agencies: { label: 'Agencies', icon: Wrench },
    snags: { label: 'Snag List', icon: AlertTriangle },
    portal: { label: 'Client Portal', icon: Link2 },
    'design-fee': { label: 'Design Fee', icon: IndianRupee },
  };

  const baseTabOrder = project.tab_order?.length ? project.tab_order : defaultTabOrder;
  // Design Fee tab needs financials permission. Team members see only tabs allowed by their permissions.
  const perms = user?.permissions;
  const tabKeyMap = { snags: 'snagList', portal: 'clientPortal' };
  const canSeeTab = (id) => {
    if (id === 'design-fee') return canFinancials;
    if (isOwner(user)) return true;
    if (!perms?.tabs) return true;
    return perms.tabs[tabKeyMap[id] || id] !== false;
  };
  const tabOrder = (() => {
    const base = baseTabOrder.filter(id => id !== 'design-fee' && canSeeTab(id));
    return canFinancials ? [...base, 'design-fee'] : base;
  })();
  const tabs = tabOrder.map(id => ({ id, ...tabMeta[id] })).filter(t => t.label);

  const enterReorder = () => { setReorderTabs([...tabOrder]); setReorderMode(true); };
  const exitReorder = async (save) => {
    if (save) {
      try {
        await api.put(`/projects/${projectId}/tab-order`, { tab_order: reorderTabs });
        setProject(prev => ({ ...prev, tab_order: reorderTabs }));
        toast.success('Tab order saved');
      } catch { toast.error('Save failed'); }
    }
    setReorderMode(false);
  };
  const resetOrder = async () => {
    try {
      await api.put(`/projects/${projectId}/tab-order`, { tab_order: [] });
      setProject(prev => ({ ...prev, tab_order: [] }));
      toast.success('Reset to default order');
    } catch { toast.error('Reset failed'); }
    setReorderMode(false);
  };
  const handleReorderDragStart = (idx) => setDragIdx(idx);
  const handleReorderDrop = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const updated = [...reorderTabs];
    const [moved] = updated.splice(dragIdx, 1);
    updated.splice(targetIdx, 0, moved);
    setReorderTabs(updated);
    setDragIdx(null);
  };

  // Proposal link
  const loadProposals = async (searchText = '') => {
    try {
      const res = await api.get(`/proposals/all?client_name=${encodeURIComponent(searchText)}`);
      setProposals(res.data || []);
    } catch (err) { console.error('Failed to load proposals:', err); }
  };

  const linkProposal = async (p) => {
    try {
      await api.put(`/projects/${projectId}/proposal-link`, {
        linked_proposal: { lead_id: p.lead_id, proposal_id: p.proposal_id, title: p.title, status: p.status, total_amount: p.total_amount, created_at: p.created_at, client_name: p.client_name }
      });
      setProject(prev => ({ ...prev, linked_proposal: { lead_id: p.lead_id, proposal_id: p.proposal_id, title: p.title, status: p.status, total_amount: p.total_amount, created_at: p.created_at, client_name: p.client_name } }));
      toast.success('Proposal linked');
      setShowLinkProposal(false);
    } catch { toast.error('Failed to link proposal'); }
  };

  const unlinkProposal = async () => {
    try {
      await api.put(`/projects/${projectId}/proposal-link`, { linked_proposal: null });
      setProject(prev => ({ ...prev, linked_proposal: null }));
      toast.success('Proposal unlinked');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-4">
          <button onClick={() => navigate('/projects')} className="mt-1 p-1.5 rounded-lg hover:bg-[#E8E6E1]/50 transition-colors"><ArrowLeft size={20} strokeWidth={1.5} /></button>
          <div>
            <div className="flex items-center gap-3">
              <InlineEdit value={project.name} onSave={v => updateField('name', v)} className="text-2xl font-['Manrope'] font-bold text-[#2C2C2E]" testId="inline-project-name" />
              <Badge className={`text-[10px] font-normal ${project.status === 'Active' ? 'bg-[#3A8C8C]/10 text-[#3A8C8C]' : 'bg-[#E8E6E1] text-[#6B7280]'}`}>{project.status}</Badge>
              <InlineEdit value={project.project_type} onSave={v => updateField('project_type', v)} type="select" options={['Residential','Commercial','Retail','Other']} className="text-[10px]" testId="inline-project-type" />
            </div>
            <div className="flex items-center gap-4 mt-1.5 text-xs text-[#6B7280]">
              <InlineEdit value={project.client_name} onSave={v => updateField('client_name', v)} className="text-xs text-[#6B7280]" testId="inline-client-name" />
              <InlineEdit value={project.client_phone} onSave={v => updateField('client_phone', v)} className="text-xs text-[#6B7280]" placeholder="Add phone" prefix="" testId="inline-client-phone" />
              <InlineEdit value={project.budget} onSave={v => updateField('budget', v)} type="number" displayValue={`${formatINR(project.budget)}`} className="text-xs text-[#6B7280]" testId="inline-budget" />
              <InlineEdit value={project.start_date?.slice(0,10)} onSave={v => updateField('start_date', v)} type="date" displayValue={project.start_date ? `${formatDate(project.start_date)} — ${formatDate(project.end_date)}` : 'Set dates'} className="text-xs text-[#6B7280]" testId="inline-start-date" />
            </div>
            {/* Site Address (Patch 15) */}
            <div className="flex items-center gap-1.5 mt-2">
              <MapPin size={12} strokeWidth={1.5} className="text-[#6B7280]" />
              <InlineEdit 
                value={project.site_address || ''} 
                onSave={v => updateField('site_address', v)} 
                className="text-xs text-[#6B7280]" 
                placeholder="Add site address" 
                testId="inline-site-address" 
              />
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Select value={project.status} onValueChange={updateStatus}>
            <SelectTrigger className="h-9 w-32 rounded-xl border-[#D4D0C8] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{['Active','On Hold','Completed','Archived'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <button
            onClick={openClientWhatsApp}
            className="p-2 rounded-xl hover:bg-[#25D366]/10 transition-colors"
            title="Send WhatsApp to client"
            data-testid="header-whatsapp-btn"
          >
            <MessageSquare size={18} strokeWidth={1.5} className="text-[#25D366]" />
          </button>
          <button
            onClick={() => { if (!openEmail(project.client_email, `Regarding ${project.name}`)) toast.error('No client email on this project'); }}
            className="p-2 rounded-xl hover:bg-[#E8E6E1]/50 transition-colors" title="Email client"
          ><Mail size={18} strokeWidth={1.5} className="text-[#6B7280]" /></button>
          {ownerOnly && (
            <button
              onClick={() => setShowInvoice(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors"
              title="Create an invoice on your letterhead" data-testid="create-invoice-btn"
            ><FileText size={15} strokeWidth={1.5} /> Invoice</button>
          )}
        </div>
      </div>
      {ownerOnly && <InvoiceDialog project={project} open={showInvoice} onClose={() => setShowInvoice(false)} />}
      {confirmDialog}

      {/* Phase bar */}
      <div className="glass-card rounded-2xl p-4" data-testid="project-phase-selector">
        <div className="flex gap-1">
          {phases.map((p, i) => (
            <button key={p} onClick={() => updatePhase(p)} className={`flex-1 py-2.5 text-xs font-medium rounded-xl text-center transition-all ${i <= phaseIdx ? 'bg-[#3A8C8C] text-white' : 'bg-[#E8E6E1] text-[#6B7280] hover:bg-[#D4D0C8]'}`}>
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <MeetingsProvider key={projectId} projectId={projectId}>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {reorderMode ? (
          /* Reorder Mode */
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-[#2C2C2E]">Drag to reorder tabs</p>
              <div className="flex gap-2">
                <button onClick={resetOrder} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-[#6B7280] hover:bg-[#E8E6E1] transition-colors" data-testid="reorder-reset">
                  <RotateCcw size={12} /> Reset
                </button>
                <button onClick={() => exitReorder(false)} className="px-3 py-1.5 rounded-lg text-xs text-[#6B7280] border border-[#D4D0C8] hover:bg-[#E8E6E1] transition-colors" data-testid="reorder-cancel">Cancel</button>
                <button onClick={() => exitReorder(true)} className="flex items-center gap-1 px-4 py-1.5 rounded-lg text-xs text-white bg-[#3A8C8C] hover:bg-[#2E7A7A] transition-colors" data-testid="reorder-done">
                  <Check size={12} /> Done
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="reorder-pills">
              {reorderTabs.map((id, idx) => {
                const meta = tabMeta[id];
                if (!meta) return null;
                const Icon = meta.icon;
                return (
                  <div key={id} draggable
                    onDragStart={() => handleReorderDragStart(idx)}
                    onDragOver={e => e.preventDefault()}
                    onDrop={() => handleReorderDrop(idx)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#D4D0C8] bg-white cursor-grab active:cursor-grabbing hover:shadow-sm transition-all ${dragIdx === idx ? 'opacity-50' : ''}`}
                    data-testid={`reorder-pill-${id}`}>
                    <GripVertical size={12} strokeWidth={1.5} className="text-[#D4D0C8]" />
                    <Icon size={13} strokeWidth={1.5} className="text-[#6B7280]" />
                    <span className="text-xs font-medium text-[#2C2C2E]">{meta.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* Normal Tab Bar */
          <div className="flex items-center">
            <TabsList className="bg-transparent border-b border-[#E8E6E1] rounded-none flex-1 justify-start gap-0 h-auto p-0 flex-wrap" data-testid="project-tabs">
              {tabs.map(t => (
                <TabsTrigger key={t.id} value={t.id} className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#3A8C8C] data-[state=active]:text-[#3A8C8C] data-[state=active]:bg-transparent text-[#6B7280] text-xs font-medium px-4 py-3 flex items-center gap-1.5 whitespace-nowrap">
                  <t.icon size={14} strokeWidth={1.5} /> {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {canManage && (
              <button onClick={enterReorder} className="p-2 rounded-lg hover:bg-[#E8E6E1] transition-colors shrink-0 ml-1" title="Reorder tabs" data-testid="reorder-tabs-btn">
                <GripVertical size={16} strokeWidth={1.5} className="text-[#6B7280]" />
              </button>
            )}
          </div>
        )}

        {/* Overview */}
        <TabsContent value="overview" className="mt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="project-overview">
            <div className="glass-card rounded-xl p-5">
              <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-3">Project Summary</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Phase</span><span className="font-medium text-[#2C2C2E]">{project.phase}</span></div>
                <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Budget</span><span className="font-medium text-[#2C2C2E]">{formatINR(project.budget)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Timeline</span><span className="font-medium text-[#2C2C2E]">{formatDate(project.start_date)} — {formatDate(project.end_date)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Meetings</span><span className="font-medium text-[#2C2C2E]">{project.meetings?.length || 0}</span></div>
              </div>
            </div>
            <div className="glass-card rounded-xl p-5">
              <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-3">Billing Overview</h4>
              {project.billing?.length > 0 ? (() => {
                const totals = project.billing.reduce((acc, b) => {
                  const paid = (b.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
                  acc.billed += Number(b.bill_amount || b.amount || 0);
                  acc.paid += paid;
                  return acc;
                }, { billed: 0, paid: 0 });
                const pending = Math.max(0, totals.billed - totals.paid);
                return (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Billed</span><span className="font-medium text-[#2C2C2E]">Rs {formatINR(totals.billed)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Paid</span><span className="font-medium text-[#7DA68A]">Rs {formatINR(totals.paid)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-[#6B7280]">Pending</span><span className={`font-medium ${pending > 0 ? 'text-[#C67D5B]' : 'text-[#7DA68A]'}`}>Rs {formatINR(pending)}</span></div>
                    <div className="text-[10px] text-[#6B7280] pt-1 border-t border-[#E8E6E1] mt-2">{project.billing.length} {project.billing.length === 1 ? 'entry' : 'entries'}</div>
                  </div>
                );
              })() : <p className="text-xs text-[#6B7280]">No billing entries</p>}
            </div>
            <div className="glass-card rounded-xl p-5">
              <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-3">Recent Activity</h4>
              <div className="space-y-2">
                {project.meetings?.slice(0, 3).map(m => (
                  <div key={m.id} className="text-xs text-[#6B7280]">Meeting on {formatDate(m.date)} — {m.agenda}</div>
                ))}
                {(!project.meetings || project.meetings.length === 0) && <p className="text-xs text-[#6B7280]">No activity yet</p>}
              </div>
            </div>
          </div>

          {/* Proposal Section — Owner-only */}
          {ownerOnly && (
          <div className="glass-card rounded-xl p-5" data-testid="proposal-section">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Proposal</h4>
              {ownerOnly && !project.linked_proposal && (
                <button onClick={() => { loadProposals(''); setShowLinkProposal(true); }}
                  className="flex items-center gap-1 text-xs text-[#3A8C8C] hover:underline" data-testid="link-proposal-btn">
                  <Link2 size={12} /> Link Proposal
                </button>
              )}
            </div>
            {project.linked_proposal ? (
              <div className="flex items-center justify-between p-3 rounded-xl bg-[#FAFAF8] border border-[#E8E6E1]" data-testid="linked-proposal-row">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#2C2C2E]">{project.linked_proposal.title || 'Design Proposal'}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[10px] text-[#6B7280]">{formatDate(project.linked_proposal.created_at)}</span>
                    <Badge className={`text-[9px] ${project.linked_proposal.status === 'Approved' ? 'bg-[#7DA68A]/10 text-[#7DA68A]' : project.linked_proposal.status === 'Sent' ? 'bg-[#3A8C8C]/10 text-[#3A8C8C]' : 'bg-[#E8E6E1] text-[#2C2C2E]'}`}>
                      {project.linked_proposal.status || 'Draft'}
                    </Badge>
                    {project.linked_proposal.total_amount > 0 && (
                      <span className="text-[10px] text-[#6B7280]">Rs {formatINR(project.linked_proposal.total_amount)}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => navigate(`/leads`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors"
                    data-testid="view-proposal-btn">
                    <ExternalLink size={12} /> View Proposal
                  </button>
                  {ownerOnly && (
                    <button onClick={unlinkProposal}
                      className="p-1.5 rounded-lg hover:bg-[#C4727F]/10 transition-colors" title="Unlink proposal" data-testid="unlink-proposal-btn">
                      <Unlink size={14} strokeWidth={1.5} className="text-[#C4727F]" />
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <FileText size={24} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-2" />
                <p className="text-xs text-[#6B7280]">No proposal linked — proposals are created in the Leads view and carried over on conversion.</p>
              </div>
            )}
          </div>
          )}

          {/* Link Proposal Modal */}
          <Dialog open={showLinkProposal} onOpenChange={setShowLinkProposal}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-['Manrope']">Link Proposal</DialogTitle></DialogHeader>
              <Input placeholder="Search by client name..." value={proposalSearch}
                onChange={e => { setProposalSearch(e.target.value); loadProposals(e.target.value); }}
                className="mt-2 h-9 rounded-lg border-[#D4D0C8] text-sm" data-testid="proposal-search" />
              <div className="max-h-[300px] overflow-y-auto mt-3 space-y-2" data-testid="proposal-list">
                {proposals.length > 0 ? proposals.map(p => (
                  <button key={`${p.lead_id}-${p.proposal_id}`} onClick={() => linkProposal(p)}
                    className="w-full text-left p-3 rounded-xl border border-[#E8E6E1] hover:border-[#3A8C8C] hover:bg-[#3A8C8C]/5 transition-colors"
                    data-testid={`proposal-option-${p.proposal_id}`}>
                    <p className="text-sm font-medium text-[#2C2C2E]">{p.title || 'Design Proposal'}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-[10px] text-[#6B7280]">{p.client_name}</span>
                      <Badge className="text-[9px] bg-[#E8E6E1] text-[#2C2C2E]">{p.status}</Badge>
                      {p.total_amount > 0 && <span className="text-[10px] text-[#6B7280]">Rs {formatINR(p.total_amount)}</span>}
                    </div>
                  </button>
                )) : (
                  <p className="text-xs text-[#6B7280] text-center py-4">No proposals found</p>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Brief */}
        <TabsContent value="brief" className="mt-6">
          <ClientBriefTab projectId={projectId} />
        </TabsContent>

        {/* Timeframe (Gantt Chart) */}
        <TabsContent value="timeframe" className="mt-6">
          <TimeframeModule 
            projectId={projectId} 
            projectStartDate={project.start_date}
            projectEndDate={project.end_date}
          />
        </TabsContent>

        {/* Drawings */}
        <TabsContent value="drawings" className="mt-6">
          <ProjectDrawings projectId={projectId} />
        </TabsContent>

        {/* Photos */}
        <TabsContent value="photos" className="mt-6">
          <ProjectPhotos projectId={projectId} />
        </TabsContent>

        {/* Meetings */}
        <TabsContent value="meetings" className="mt-6">
          <MeetingsTab
            clientName={project.client_name || ''}
            clientPhone={project.client_phone || ''}
            projectName={project.name || ''}
            senderName={user?.name ? `${user.name} · Interior Story` : 'Interior Story'}
          />
        </TabsContent>

        {/* Tasks */}
        <TabsContent value="tasks" className="mt-6">
          <ProjectTasksBoard projectId={projectId} projectName={project.name} />
        </TabsContent>

        {/* Agencies */}
        <TabsContent value="agencies" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="section-heading text-sm font-semibold font-['Manrope']">Agencies</h4>
            <button onClick={() => openAdd('agency')} className="text-xs text-[#3A8C8C] flex items-center gap-1 hover:underline"><Plus size={14} /> Add Agency</button>
          </div>
          <div className="space-y-3" data-testid="agencies-list">
            {project.agencies?.map(a => (
              <div key={a.id} className="glass-card rounded-xl p-4 flex items-center justify-between">
                <div>
                  <InlineVendorEdit
                    value={a.name}
                    onSave={(v, vendor) => updateSubItem('agencies', a.id, {
                      name: v,
                      contact: vendor?.phone || a.contact || '',
                    })}
                    className="text-sm font-medium text-[#2C2C2E]"
                    testId={`agency-vendor-${a.id}`}
                  />
                  <div className="flex items-center gap-1 mt-0.5">
                    <InlineEdit value={a.scope} onSave={v => updateSubItem('agencies', a.id, { scope: v })} className="text-xs text-[#6B7280]" placeholder="Scope" />
                    <span className="text-xs text-[#D4D0C8]">&middot;</span>
                    <InlineEdit value={a.contact} onSave={v => updateSubItem('agencies', a.id, { contact: v })} className="text-xs text-[#6B7280]" placeholder="Contact" />
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {canManage && (
                    <button
                      onClick={() => generateVendorPortal(a)}
                      className="p-1.5 rounded-lg hover:bg-[#3A8C8C]/10 transition-colors"
                      title="Generate vendor portal link"
                      data-testid={`agency-portal-btn-${a.id}`}
                    >
                      <Link2 size={14} strokeWidth={1.5} className="text-[#3A8C8C]" />
                    </button>
                  )}
                  <button
                    onClick={() => openVendorWhatsApp(a)}
                    className="p-1.5 rounded-lg hover:bg-[#25D366]/10 transition-colors"
                    title="Send WhatsApp to vendor"
                    data-testid={`agency-whatsapp-btn-${a.id}`}
                  >
                    <MessageSquare size={14} strokeWidth={1.5} className="text-[#25D366]" />
                  </button>
                  <button
                    onClick={() => removeAgency(a.id)}
                    className="p-1.5 rounded-lg hover:bg-[#C4727F]/10 transition-colors"
                    title="Remove vendor from project"
                    data-testid={`agency-delete-btn-${a.id}`}
                  >
                    <Trash2 size={14} strokeWidth={1.5} className="text-[#C4727F]" />
                  </button>
                </div>
              </div>
            ))}
            {(!project.agencies || project.agencies.length === 0) && <p className="text-sm text-[#6B7280] text-center py-6">No agencies linked</p>}
          </div>
        </TabsContent>

        {/* Billing */}
        <TabsContent value="billing" className="mt-6">
          <ProjectBilling
            projectId={projectId}
            projectName={project.name}
            billingData={project.billing || []}
            onUpdate={(billing) => setProject(prev => ({ ...prev, billing }))}
          />
        </TabsContent>

        {/* Orders */}
        <TabsContent value="orders" className="mt-6">
          <ProjectOrders
            projectId={projectId}
            projectName={project.name}
            ordersData={project.orders || []}
            billingData={project.billing || []}
            onUpdate={(orders) => setProject(prev => ({ ...prev, orders }))}
            onBillingUpdate={(billing) => setProject(prev => ({ ...prev, billing }))}
          />
        </TabsContent>

        {/* Materials */}
        <TabsContent value="materials" className="mt-6">
          <ProjectMaterials
            projectId={projectId}
            projectName={project.name}
            projectType={project.project_type}
            materialsData={project.materials || []}
            onUpdate={(materials) => setProject(prev => ({ ...prev, materials }))}
          />
        </TabsContent>

        {/* Snag List */}
        <TabsContent value="snags" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="section-heading text-sm font-semibold font-['Manrope']">Snag List</h4>
            <button onClick={() => openAdd('snag')} className="text-xs text-[#3A8C8C] flex items-center gap-1 hover:underline"><Plus size={14} /> Add Snag</button>
          </div>
          <div className="space-y-3" data-testid="snag-list">
            {project.snag_list?.map(s => (
              <div key={s.id} className="glass-card rounded-xl p-4 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <InlineEdit value={s.description || s.location} onSave={v => updateSubItem('snag_list', s.id, { description: v })} className="text-sm font-medium text-[#2C2C2E]" />
                  <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                    <InlineEdit value={s.location} onSave={v => updateSubItem('snag_list', s.id, { location: v })} className="text-xs text-[#6B7280]" placeholder="Location" />
                    <span className="text-xs text-[#D4D0C8]">&middot;</span>
                    <span className="text-xs text-[#6B7280]">Assigned:</span>
                    <InlineVendorEdit
                      value={s.vendor_name}
                      onSave={v => updateSubItem('snag_list', s.id, { vendor_name: v })}
                      placeholder="Assign vendor"
                      className="text-xs text-[#6B7280]"
                      testId={`snag-vendor-${s.id}`}
                    />
                    <span className="text-xs text-[#D4D0C8]">&middot;</span>
                    <span className="text-xs text-[#6B7280]">{formatDate(s.created_at)}</span>
                  </div>
                </div>
                <InlineEdit value={s.status} onSave={v => updateSubItem('snag_list', s.id, { status: v })} type="select" options={['Open','In Progress','Resolved']} className={`text-[10px] px-2 py-0.5 rounded-full ${snagStatus[s.status] || ''}`} />
              </div>
            ))}
            {(!project.snag_list || project.snag_list.length === 0) && <p className="text-sm text-[#6B7280] text-center py-6">No snag items</p>}
          </div>
        </TabsContent>

        {/* BOQ / Estimate */}
        <TabsContent value="boq" className="mt-6">
          <BOQEstimateEditor projectId={projectId} />
        </TabsContent>

        {/* Client Portal */}
        <TabsContent value="portal" className="mt-6">
          <ClientPortalManager projectId={projectId} projectName={project.name} />
        </TabsContent>

        {/* Design Fee — requires financials permission */}
        {canFinancials && (
          <TabsContent value="design-fee" className="mt-6">
            <DesignFeeTab project={project} />
          </TabsContent>
        )}
      </Tabs>
      </MeetingsProvider>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-['Manrope'] capitalize">Add {addType}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {addType === 'meeting' && <>
              <div><Label className="text-xs text-[#6B7280] uppercase">Date</Label><Input type="date" onChange={e => setAddForm({...addForm, date: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Location</Label><Input onChange={e => setAddForm({...addForm, location: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Agenda</Label><Input onChange={e => setAddForm({...addForm, agenda: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Decisions</Label><Input onChange={e => setAddForm({...addForm, decisions: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
            </>}
            {addType === 'billing' && <>
              <div><Label className="text-xs text-[#6B7280] uppercase">Milestone</Label><Input onChange={e => setAddForm({...addForm, milestone: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Description</Label><Input onChange={e => setAddForm({...addForm, description: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-[#6B7280] uppercase">Amount</Label><Input type="number" onChange={e => setAddForm({...addForm, amount: Number(e.target.value)})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
                <div><Label className="text-xs text-[#6B7280] uppercase">Invoice Date</Label><Input type="date" onChange={e => setAddForm({...addForm, invoice_date: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              </div>
            </>}
            {addType === 'order' && <>
              <div><Label className="text-xs text-[#6B7280] uppercase">Item</Label><Input onChange={e => setAddForm({...addForm, item: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-[#6B7280] uppercase">Vendor</Label><Input onChange={e => setAddForm({...addForm, vendor: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
                <div><Label className="text-xs text-[#6B7280] uppercase">Quantity</Label><Input onChange={e => setAddForm({...addForm, quantity: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-[#6B7280] uppercase">Amount</Label><Input type="number" onChange={e => setAddForm({...addForm, amount: Number(e.target.value)})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
                <div><Label className="text-xs text-[#6B7280] uppercase">Expected Date</Label><Input type="date" onChange={e => setAddForm({...addForm, expected_date: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              </div>
            </>}
            {addType === 'snag' && <>
              <div><Label className="text-xs text-[#6B7280] uppercase">Location</Label><Input onChange={e => setAddForm({...addForm, location: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Description</Label><textarea onChange={e => setAddForm({...addForm, description: e.target.value})} className="mt-1 w-full h-20 px-4 py-2 bg-white border border-[#D4D0C8] rounded-xl text-sm resize-none" /></div>
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Assign Vendor (optional)</Label>
                <div className="mt-1">
                  <VendorSelect
                    value={addForm.vendor_name || ''}
                    onChange={(name) => setAddForm({ ...addForm, vendor_name: name })}
                    placeholder="Search or select vendor to fix this"
                    testId="snag-form-vendor"
                  />
                </div>
              </div>
            </>}
            {addType === 'design' && <>
              <div><Label className="text-xs text-[#6B7280] uppercase">Stage Name</Label><Input onChange={e => setAddForm({...addForm, name: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Status</Label>
                <Select onValueChange={v => setAddForm({...addForm, status: v})}><SelectTrigger className="mt-1 h-10 rounded-xl border-[#D4D0C8]"><SelectValue placeholder="Select status" /></SelectTrigger><SelectContent>{['Created','Sent to Client','Revisions','Client Approved'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
            </>}
            {addType === 'drawing' && <>
              <div><Label className="text-xs text-[#6B7280] uppercase">Drawing Name</Label><Input onChange={e => setAddForm({...addForm, name: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Link (URL)</Label><Input onChange={e => setAddForm({...addForm, link: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
            </>}
            {addType === 'agency' && <>
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Vendor / Agency *</Label>
                <div className="mt-1">
                  <VendorSelect
                    value={addForm.name || ''}
                    onChange={(name, vendor) => setAddForm(prev => ({
                      ...prev,
                      name,
                      contact: vendor?.phone || prev.contact || '',
                    }))}
                    placeholder="Search or select vendor"
                    testId="agency-form-vendor"
                  />
                </div>
              </div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Contact (optional)</Label><Input value={addForm.contact || ''} onChange={e => setAddForm(prev => ({...prev, contact: e.target.value}))} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Scope</Label><Input value={addForm.scope || ''} onChange={e => setAddForm(prev => ({...prev, scope: e.target.value}))} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
            </>}
            <button onClick={submitAdd} className="w-full h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors">Add</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!whatsappTarget} onOpenChange={(o) => !o && setWhatsappTarget(null)}>
        <DialogContent className="max-w-xl p-0 bg-transparent border-none shadow-none" data-testid="whatsapp-composer-dialog">
          <DialogHeader className="sr-only"><DialogTitle>WhatsApp Composer</DialogTitle></DialogHeader>
          {whatsappTarget && (
            <WhatsAppDeepLinkButton
              clientName={whatsappTarget.name}
              clientPhone={whatsappTarget.phone}
              projectName={project.name}
              updateText=""
              senderName={user?.name ? `${user.name} · Interior Story` : 'Interior Story'}
              template={whatsappTarget.kind === 'vendor' ? 'custom' : 'statusUpdate'}
              defaultCountry="91"
              onSent={(payload) => {
                api.post(`/projects/${projectId}/activity`, {
                  type: 'whatsapp_sent',
                  target: whatsappTarget.kind,
                  contact_name: whatsappTarget.name,
                  ...payload,
                }).catch(() => { /* activity log is best-effort */ });
                toast.success(`Opening WhatsApp for ${whatsappTarget.name}`);
                setWhatsappTarget(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>


      <Dialog open={!!vendorPortalModal} onOpenChange={(o) => !o && setVendorPortalModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-['Manrope']">Vendor Portal Link</DialogTitle></DialogHeader>
          {vendorPortalModal && (
            <div className="space-y-3 mt-2" data-testid="vendor-portal-modal">
              <p className="text-xs text-[#6B7280]">
                Share this read-only link with <span className="font-medium text-[#2C2C2E]">{vendorPortalModal.agency.name}</span> &mdash; they&apos;ll see drawings &amp; photos for <span className="font-medium text-[#2C2C2E]">{project.name}</span> only.
              </p>
              <div className="flex items-center gap-2 p-2 bg-[#FAFAF8] rounded-lg border border-[#E8E6E1]">
                <input
                  readOnly
                  value={`${window.location.origin}/vendor-portal/${vendorPortalModal.link.token}`}
                  className="flex-1 bg-transparent text-xs text-[#2C2C2E] outline-none"
                  data-testid="vendor-portal-url"
                />
                <button
                  onClick={() => copyVendorPortalUrl(vendorPortalModal.link.token)}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#3A8C8C] text-white text-[10px] font-medium hover:bg-[#2E7A7A]"
                  data-testid="vendor-portal-copy"
                >
                  <Copy size={11} /> Copy
                </button>
              </div>
              <p className="text-[10px] text-[#6B7280]">
                Active until manually revoked. Manage all vendor links in the <span className="text-[#3A8C8C]">Client Portal</span> tab.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ClientPortalManager({ projectId, projectName }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const portalBase = `${window.location.origin}/portal`;

  const loadLinks = async () => {
    try { const res = await api.get(`/projects/${projectId}/portal/links`); setLinks(res.data); }
    catch { /* portal links unavailable */ } finally { setLoading(false); }
  };

  useEffect(() => { loadLinks(); }, [projectId]);

  const generateLink = async () => {
    try {
      const res = await api.post(`/projects/${projectId}/portal/generate`);
      toast.success('Portal link generated');
      loadLinks();
    } catch { toast.error('Failed to generate link'); }
  };

  const revokeLink = async (linkId) => {
    try { await api.post(`/portal/links/${linkId}/revoke`); toast.success('Link revoked'); loadLinks(); }
    catch { toast.error('Revoke failed'); }
  };

  const refreshLink = async (linkId) => {
    try { await api.post(`/portal/links/${linkId}/refresh`); toast.success('Link refreshed for 30 more days'); loadLinks(); }
    catch { toast.error('Refresh failed'); }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${portalBase}/${token}`);
    toast.success('Link copied to clipboard');
  };

  if (loading) return <div className="flex items-center justify-center h-20"><div className="w-5 h-5 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="client-portal-manager">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="section-heading text-sm font-semibold font-['Manrope']">Client Portal</h4>
          <p className="text-xs text-[#6B7280] mt-0.5">Share a read-only portal link with your client. No login required.</p>
        </div>
        <button onClick={generateLink} data-testid="generate-portal-link-btn"
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors">
          <Link2 size={13} strokeWidth={1.5} /> Generate Link
        </button>
      </div>

      <div className="space-y-3">
        {links.map(link => {
          const isExpired = link.expires_at && new Date(link.expires_at) < Date.now();
          return (
            <div key={link.id} className="glass-card rounded-xl p-4" data-testid={`portal-link-${link.id}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-[#3A8C8C] bg-[#3A8C8C]/5 px-2 py-1 rounded-md truncate block max-w-md">
                      {portalBase}/{link.token}
                    </code>
                    <button onClick={() => copyLink(link.token)} className="p-1 rounded-lg hover:bg-[#E8E6E1] transition-colors" data-testid={`copy-link-${link.id}`}>
                      <Copy size={13} strokeWidth={1.5} className="text-[#6B7280]" />
                    </button>
                  </div>
                </div>
                <Badge className={isExpired ? 'bg-[#C4727F]/10 text-[#C4727F] text-[10px]' : 'bg-[#7DA68A]/10 text-[#7DA68A] text-[10px]'}>
                  {isExpired ? 'Expired' : 'Active'}
                </Badge>
              </div>
              <div className="flex items-center justify-between text-[10px] text-[#6B7280]">
                <span>Created: {formatDate(link.created_at)} &middot; Expires: {formatDate(link.expires_at)}</span>
                <div className="flex gap-2">
                  <button onClick={() => refreshLink(link.id)} className="text-[#3A8C8C] hover:underline">Refresh</button>
                  <button onClick={() => revokeLink(link.id)} className="text-[#C4727F] hover:underline">Revoke</button>
                </div>
              </div>
            </div>
          );
        })}
        {links.length === 0 && (
          <div className="text-center py-8">
            <Link2 size={28} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-3" />
            <p className="text-sm text-[#6B7280]">No portal links generated yet.</p>
            <p className="text-xs text-[#D4D0C8] mt-1">Generate a link to share project progress with your client.</p>
          </div>
        )}
      </div>
    </div>
  );
}
